//----- Component --------------------------------------------------------/
// Developer : Daiana Curcio/Nicolas Pascucci
// Creation Date : 13/03/2024
// Version : 2
// Description : Tabla para ver los viajes pendientes de las plantas
//------Changes ----------------------------------------------------------//
// Change Date : 04/11/2026
// Change by :
// Change description: Diseño compacto, filtro de pendientes, avisos de derivación y de error
//========================================================================/
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Navbar";
import { UrlApi } from "../../services/apirest";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es"; //Tabla en español
import {
  Alert,
  Box,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile"; //ADJUNTAR IMAGEN
import HdIcon from "@mui/icons-material/Hd";
import Dialog_recibido from "./Dialog_recibido";
import DetalleViaje from "./DetalleViaje";
import Historial_derivaciones from "./Historial_derivaciones";
import FileUploadStock from "../stock_almacen/FileUploadStock"; //ADJUNTAR IMAGEN
import { logEventos } from "../../services/services";

const DIAS_PENDIENTES = 90;

const COLOR = {
  fondo: "#E0F2F1",
  borde: "#00796B",
  encabezado: "#B2DFDB",
};

const obtenerTextoDerivacion = (row) => {
  if (row.solo_informativo === 1) {
    return "Derivado a " + row.nombre_sucursal;
  }

  if (row.derivado === 1 && row.nombre_sucursal_anterior) {
    return "Derivado desde " + row.nombre_sucursal_anterior;
  }

  return "";
};

const esViajePendiente = (viaje) => {
  const fechaLimite = moment().subtract(DIAS_PENDIENTES, "days").startOf("day");

  return (
    viaje.estado !== 233 &&
    viaje.recibido !== 1 &&
    Boolean(viaje.fecha) &&
    moment(viaje.fecha, "YYYY-MM-DD").isSameOrAfter(fechaLimite)
  );
};

const ordenarViajes = (viajes) =>
  [...viajes].sort((a, b) => {
    const avisoA = a.solo_informativo === 1 ? 1 : 0;
    const avisoB = b.solo_informativo === 1 ? 1 : 0;

    if (avisoA !== avisoB) return avisoB - avisoA;

    return Number(b.id_viaje) - Number(a.id_viaje);
  });

function Sucursal_viajes() {
  const [viajes, setViajes] = useState();
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [row, setRow] = useState();
  const [newUuid, setNewUuid] = useState(""); //ADJUNTAR IMAGEN
  const [openCloseAdj, setOpenCloseAdj] = useState(false); //ADJUNTAR IMAGEN
  const [abrirDialogRecibido, setAbrirDialogRecibido] = useState(false);
  const [abrirDialogHistorial, setAbrirDialogHistorial] = useState(false);
  const [alerta, setAlerta] = useState("");

  useEffect(() => {
    logEventos(
      "Acceso al menú viajes a sucursales",
      "Viajes a Sucursales",
      "Ver los viajes pendientes a las sucursales autorizadas",
      localStorage.getItem("user")
    );
  }, []);

  const pedirLista = async (ruta, sucursales) => {
    const res = await fetch(UrlApi + ruta + sucursales);
    const data = await res.json().catch(() => null);

    // Las rutas viejas pueden responder 200 con un error, por eso se valida que sea una lista.
    if (!res.ok || !Array.isArray(data)) {
      throw new Error("Respuesta inválida en " + ruta);
    }

    return data;
  };

  const obtenerViajes = async () => {
    const sucursales = localStorage.getItem("suc_auth");

    setCargando(true);
    setErrorCarga("");

    const [noRecibidos, derivadosSalientes] = await Promise.allSettled([
      pedirLista("/viajes_abastecimiento/viajes_suc/No_Rec/", sucursales),
      pedirLista(
        "/viajes_abastecimiento/viajes_suc/derivados_salientes/",
        sucursales
      ),
    ]);

    if (noRecibidos.status === "rejected") {
      console.error(noRecibidos.reason);
      setViajes(null);
      setErrorCarga(
        "No se pudieron cargar los viajes. Recargá la pantalla o avisá a Sistemas."
      );
      setCargando(false);
      return;
    }

    let avisos = [];

    if (derivadosSalientes.status === "fulfilled") {
      avisos = derivadosSalientes.value;
    } else {
      console.error(derivadosSalientes.reason);
      setAlerta(
        "No se pudieron cargar los avisos de viajes derivados. El resto del listado está disponible."
      );
    }

    setViajes(
      ordenarViajes([...avisos, ...noRecibidos.value.filter(esViajePendiente)])
    );
    setCargando(false);
  };

  useEffect(() => {
    obtenerViajes();
  }, []);

  const handleClickOpenRecibido = (fila) => {
    setRow(fila);
    setAbrirDialogRecibido(true);
  };

  const handleCloseRecibido = () => {
    setAbrirDialogRecibido(false);
    obtenerViajes();
  };

  const handleClickOpenHistorial = (fila) => {
    setRow(fila);
    setAbrirDialogHistorial(true);
  };

  const handleCloseHistorial = () => {
    setAbrirDialogHistorial(false);
  };

  const handleCerrarAlerta = (event, reason) => {
    if (reason === "clickaway") return;
    setAlerta("");
  };

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id_viaje", size: 60 },
      { header: "Salida", accessorKey: "fecha", size: 85 },
      { header: "Llegada", accessorKey: "fecha_llegada", size: 85 },
      { header: "Producto", accessorKey: "tipo_viaje", size: 85 },
      {
        header: "Proveedor",
        id: "proveedor_completo",
        accessorFn: (fila) => fila.prov_boca + " - " + fila.nombre_boca,
        size: 170,
      },
      { header: "Empresa", accessorKey: "empresa_nombre", size: 95 },
      { header: "Destino", accessorKey: "nombre_sucursal", size: 120 },
      {
        header: "Derivación",
        id: "derivacion",
        accessorFn: obtenerTextoDerivacion,
        size: 165,
      },
      { header: "Estado", accessorKey: "estado_palabra", size: 110 },
    ],
    []
  );

  return (
    <>
      <Navbar />

      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          bgcolor: "grey.50",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        {errorCarga ? <Alert severity="error">{errorCarga}</Alert> : null}

        {viajes !== null ? (
          <MaterialReactTable
            columns={columns}
            data={viajes || []}
            localization={MRT_Localization_ES}
            layoutMode="grid"
            state={{ isLoading: cargando }}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableHiding={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableStickyHeader
            enableEditing
            positionActionsColumn="last"
            displayColumnDefOptions={{
              "mrt-row-actions": {
                header: "Acciones",
                size: 110,
              },
            }}
            initialState={{
              density: "compact",
              showGlobalFilter: true,
              pagination: { pageIndex: 0, pageSize: 25 },
            }}
            muiSearchTextFieldProps={{
              placeholder: "Buscar...",
              variant: "outlined",
              size: "small",
              sx: {
                width: "12rem",
                minWidth: "12rem",
                bgcolor: "white",
                "& .MuiInputBase-input": {
                  fontSize: 12,
                  paddingTop: "4px",
                  paddingBottom: "4px",
                },
                "& .MuiSvgIcon-root": { fontSize: 16 },
              },
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                border: "1px solid",
                borderColor: "divider",
                borderTop: `3px solid ${COLOR.borde}`,
              },
            }}
            muiTopToolbarProps={{
              sx: {
                bgcolor: COLOR.fondo,
                minHeight: "2.25rem",
                "& > .MuiBox-root": { py: 0.25 },
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                bgcolor: COLOR.fondo,
                minHeight: "2.25rem",
                "& .MuiTablePagination-toolbar": {
                  minHeight: "2.25rem",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select":
                {
                  fontSize: 12,
                },
              },
            }}
            muiTableContainerProps={{
              sx: { maxHeight: "calc(100vh - 170px)", minHeight: 180 },
            }}
            muiTableHeadCellProps={{
              sx: {
                fontSize: 11,
                fontWeight: 700,
                py: 0.5,
                bgcolor: COLOR.encabezado,
              },
            }}
            muiTableBodyCellProps={{
              sx: { fontSize: 12, py: 0.25 },
            }}
            muiTableBodyRowProps={({ row: fila }) => ({
              sx:
                fila.original.solo_informativo === 1
                  ? { backgroundColor: "#FFF3CD" }
                  : {},
            })}
            renderTopToolbarCustomActions={() => (
              <Box sx={{ px: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  Viajes pendientes a sucursales autorizadas
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  Últimos {DIAS_PENDIENTES} días, sin finalizar ni recibir
                </Typography>
              </Box>
            )}
            renderDetailPanel={({ row: fila }) => (
              <DetalleViaje datos={fila.original} />
            )}
            renderRowActions={({ row: fila }) => {
              const viaje = fila.original;
              const soloInformativo = viaje.solo_informativo === 1;

              return (
                <Box sx={{ display: "flex", flexWrap: "nowrap" }}>
                  {!soloInformativo ? (
                    <Tooltip arrow title="Ver adjunto">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setNewUuid(viaje.uuid);
                          setOpenCloseAdj(true);
                        }}
                      >
                        <AttachFileIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}

                  {viaje.derivado === 1 ? (
                    <Tooltip arrow title="Historial de derivación">
                      <IconButton
                        size="small"
                        onClick={() => handleClickOpenHistorial(viaje)}
                        sx={{ color: "#1565C0" }}
                      >
                        <HdIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}

                  {!soloInformativo ? (
                    <Tooltip arrow title="Finalizar viaje">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleClickOpenRecibido(viaje)}
                      >
                        <DoneOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            }}
          />
        ) : null}
      </Box>

      <Snackbar
        open={Boolean(alerta)}
        onClose={handleCerrarAlerta}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCerrarAlerta}
          severity="warning"
          variant="filled"
        >
          {alerta}
        </Alert>
      </Snackbar>

      {abrirDialogRecibido ? (
        <Dialog_recibido
          abrirDialogRecibido={abrirDialogRecibido}
          row={row}
          onClose={handleCloseRecibido}
        />
      ) : null}

      {abrirDialogHistorial ? (
        <Historial_derivaciones
          abrirDialogHistorial={abrirDialogHistorial}
          row={row}
          onClose={handleCloseHistorial}
        />
      ) : null}

      {openCloseAdj ? ( // ADJUNTAR IMAGEN
        <FileUploadStock
          fileId={newUuid}
          openclose={true}
          mostrar={false}
          onClose={() => setOpenCloseAdj(false)}
        />
      ) : null}
    </>
  );
}

export default Sucursal_viajes;